import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, Instagram, Linkedin, Facebook } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      toast.success(data.message || "Subscribed");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-brand-ink text-brand-ivory relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #D4A017 0%, transparent 40%)" }} />

      <div className="container-luxe relative pt-24 pb-10">
        <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-brand-ivory/10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 border border-brand-gold flex items-center justify-center">
                <span className="font-serif text-2xl text-brand-gold">D</span>
              </div>
              <div>
                <div className="font-serif text-2xl leading-none">DeepRoots Spice</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-ivory/40 mt-1.5">India · Since Origin</div>
              </div>
            </div>
            <p className="text-brand-ivory/60 text-[15px] leading-relaxed max-w-sm">
              A premium Indian manufacturer, supplier and exporter — bringing the depth and character of the subcontinent's finest spices to buyers across the world.
            </p>
            <div className="flex items-center gap-4 mt-8">
              <a href="#" className="w-9 h-9 border border-brand-ivory/20 hover:border-brand-gold hover:bg-brand-gold hover:text-brand-ink flex items-center justify-center transition-all" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 border border-brand-ivory/20 hover:border-brand-gold hover:bg-brand-gold hover:text-brand-ink flex items-center justify-center transition-all" data-testid="footer-linkedin" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 border border-brand-ivory/20 hover:border-brand-gold hover:bg-brand-gold hover:text-brand-ink flex items-center justify-center transition-all" data-testid="footer-facebook" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h5 className="overline mb-6">Explore</h5>
            <ul className="space-y-3 text-[14px]">
              <li><Link to="/about" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">About</Link></li>
              <li><Link to="/products" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Spices</Link></li>
              <li><Link to="/makhana" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Makhana</Link></li>
              <li><Link to="/pulses" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Pulses</Link></li>
              <li><Link to="/quality" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Quality</Link></li>
              <li><Link to="/private-label" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Private Label</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h5 className="overline mb-6">Trade</h5>
            <ul className="space-y-3 text-[14px]">
              <li><Link to="/export-markets" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Global Markets</Link></li>
              <li><Link to="/certifications" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Certifications</Link></li>
              <li><Link to="/resources" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Resources</Link></li>
              <li><Link to="/blog" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">Journal</Link></li>
              <li><Link to="/faq" className="text-brand-ivory/70 hover:text-brand-gold transition-colors">FAQ</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h5 className="overline mb-6">Trade Enquiries</h5>
            <ul className="space-y-4 text-[14px] text-brand-ivory/70">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-brand-gold shrink-0" />
                <span>Export Office · India<br /><span className="text-brand-ivory/40 text-[12px]">Full address available on request</span></span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-brand-gold shrink-0" />
                <a href="tel:+919211974059" className="hover:text-brand-gold transition-colors" data-testid="footer-phone">+91 9211974059</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-brand-gold shrink-0" />
                <a href="mailto:info@deeprootsspice.com" className="hover:text-brand-gold transition-colors" data-testid="footer-email">info@deeprootsspice.com</a>
              </li>
            </ul>

            <form onSubmit={subscribe} className="mt-8" data-testid="newsletter-form">
              <label className="overline text-brand-ivory/60 block mb-3">Trade Bulletin</label>
              <div className="flex border-b border-brand-ivory/20 focus-within:border-brand-gold transition-colors">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@company.com"
                  className="flex-1 bg-transparent border-0 outline-none py-3 text-brand-ivory placeholder-brand-ivory/30 text-[14px]"
                  data-testid="newsletter-email-input"
                  required
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="pl-4 text-brand-gold hover:text-brand-ivory transition-colors disabled:opacity-50"
                  data-testid="newsletter-submit"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[11px] text-brand-ivory/40 mt-3">Monthly industry insights. No spam. Unsubscribe anytime.</p>
            </form>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-brand-ivory/40">
          <div>© {new Date().getFullYear()} DeepRoots Spice. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/privacy" className="hover:text-brand-gold transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-brand-gold transition-colors">Terms</Link>
            <Link to="/shipping" className="hover:text-brand-gold transition-colors">Shipping</Link>
            <span className="text-brand-ivory/25">Crafted in India · Delivered worldwide</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
